import { useState, useRef, useMemo } from 'react';
import { isURL } from 'validator';
import { isYoutubeUrl, uploadPdfToS3 } from '../../Homepage/Utils/GenerationUtils';
import { useImageUpload } from '../../../hooks/useImageUpload';
import { Mixpanel } from '../../../services/Mixpanel';

/**
 * Manages file, image, link, and YouTube attachments for the Unlimited creation flow.
 *
 * @param {Object} params
 * @param {Function} params.setError - Setter to display error messages to the user.
 * @param {Function} params.setQuery - Setter to update the text query (used when injecting YouTube transcripts).
 * @param {Object} params.inputRef - React ref for the main text input element.
 * @returns {{
 *   pdfFile: File|null,
 *   uploadedPdfURL: string|null,
 *   isUploadingPdf: boolean,
 *   sourceContent: any,
 *   handleFileInputChange: Function,
 *   clearPdf: Function,
 *   fileInputRef: Object,
 *   imageFiles: File[],
 *   uploadedImageURLs: string[],
 *   isUploadingImage: boolean,
 *   handleImageInputChange: Function,
 *   handlePaste: Function,
 *   pastedSourceText: string|null,
 *   clearImageWithAnalyses: Function,
 *   attachedLinks: string[],
 *   showLinkModal: boolean,
 *   handleAddLink: Function,
 *   clearLink: Function,
 *   showYoutubeTranscriptModal: boolean,
 *   youtubeModalUrl: string,
 *   handleYoutubeModalClose: Function,
 *   handleYoutubeTranscript: Function,
 * }}
 */
export const useAttachments = ({ setError, setQuery, inputRef }) => {
  // PDF upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadedPdfURL, setUploadedPdfURL] = useState(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [sourceContent, setSourceContent] = useState(null);

  // Image upload (shared hook)
  const {
    imageFiles,
    uploadedImageUrls: uploadedImageURLs,
    isUploadingImage,
    imageInputRef,
    maxImages: MAX_IMAGES,
    handleImageInputChange,
    handleImagePaste,
    clearImage,
  } = useImageUpload({ trackingPrefix: 'Unlimited', setError });
  const [imageAnalyses, setImageAnalyses] = useState([]);

  // Large text pastes are remembered so "Use Original Text" can preserve them
  // verbatim as source content. Below the threshold a paste is just prompt text.
  // 500 chars matches the backend's inline-content heuristic in /unlimited/follow-up.
  const PASTED_TEXT_SOURCE_THRESHOLD = 500;
  const [pastedSourceText, setPastedSourceText] = useState(null);

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (text && text.trim().length >= PASTED_TEXT_SOURCE_THRESHOLD) {
      setPastedSourceText(text.trim());
    }
    handleImagePaste(e);
  };

  // Link state
  const [attachedLinks, setAttachedLinks] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkInput, setLinkInput] = useState('');

  // YouTube state
  const [showYoutubeTranscriptModal, setShowYoutubeTranscriptModal] = useState(false);
  const [youtubeModalUrl, setYoutubeModalUrl] = useState('');
  const youtubeSourceContentCallback = useRef(null);

  const fileInputRef = useRef(null);

  const handleAddLink = () => {
    if (!isURL(linkInput)) return;
    if (isYoutubeUrl(linkInput)) {
      setYoutubeModalUrl(linkInput.trim());
      setShowLinkModal(false);
      setShowYoutubeTranscriptModal(true);
      setLinkInput('');
    } else {
      setAttachedLinks((prev) => [...prev, linkInput.trim()]);
      setLinkInput('');
      setShowLinkModal(false);
    }
  };

  const handleYoutubeModalClose = (val) => {
    setShowYoutubeTranscriptModal(val);
    if (!val) youtubeSourceContentCallback.current = null;
  };

  const handleYoutubeTranscript = (transcript) => {
    if (youtubeSourceContentCallback.current) {
      youtubeSourceContentCallback.current(transcript);
      youtubeSourceContentCallback.current = null;
      setShowYoutubeTranscriptModal(false);
      return;
    }
    setQuery((prev) => {
      const cleaned = prev
        .split(/\s+/)
        .filter((word) => !isYoutubeUrl(word.replace(/[.,;:!?)]+$/, '')))
        .join(' ')
        .trim();
      return cleaned ? `${cleaned}\n\n${transcript}` : transcript;
    });
    setAttachedLinks((prev) => prev.filter((url) => !isYoutubeUrl(url)));
    setShowYoutubeTranscriptModal(false);
    inputRef.current?.focus();
  };

  const handlePdfUpload = async (file) => {
    if (!file) return;

    if (file.size > 50 * 1000 * 1000) {
      setError('Please choose a smaller file: limit is 50MB');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file');
      return;
    }

    setPdfFile(file);
    setIsUploadingPdf(true);
    setError(null);

    try {
      const url = await uploadPdfToS3(file);
      setUploadedPdfURL(url);
      Mixpanel.track('Unlimited PDF Uploaded', {
        file_name: file.name,
        file_size: file.size,
      });
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setError('Failed to upload PDF. Please try again.');
      setPdfFile(null);
      Mixpanel.track('Unlimited PDF Upload Failed', {
        error: err.message,
      });
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handlePdfUpload(file);
    }
    e.target.value = '';
  };

  const clearPdf = () => {
    Mixpanel.track('Unlimited PDF Removed');
    setPdfFile(null);
    setUploadedPdfURL(null);
    setSourceContent(null);
  };

  const clearImageWithAnalyses = (index) => {
    clearImage(index);
    setImageAnalyses((prev) => prev.filter((_, i) => i !== index));
  };

  const clearLink = (index) => {
    Mixpanel.track('Unlimited Link Removed');
    setAttachedLinks((prev) => prev.filter((_, i) => i !== index));
  };

  return useMemo(
    () => ({
      // PDF
      pdfFile,
      uploadedPdfURL,
      setUploadedPdfURL,
      isUploadingPdf,
      sourceContent,
      setSourceContent,
      handleFileInputChange,
      clearPdf,
      fileInputRef,
      // Images
      imageFiles,
      uploadedImageURLs,
      isUploadingImage,
      imageInputRef,
      MAX_IMAGES,
      handleImageInputChange,
      handlePaste,
      pastedSourceText,
      imageAnalyses,
      setImageAnalyses,
      clearImageWithAnalyses,
      // Links
      attachedLinks,
      setAttachedLinks,
      showLinkModal,
      setShowLinkModal,
      linkInput,
      setLinkInput,
      handleAddLink,
      clearLink,
      // YouTube
      showYoutubeTranscriptModal,
      youtubeModalUrl,
      setYoutubeModalUrl,
      setShowYoutubeTranscriptModal,
      youtubeSourceContentCallback,
      handleYoutubeModalClose,
      handleYoutubeTranscript,
    }),
    [
      pdfFile,
      uploadedPdfURL,
      isUploadingPdf,
      sourceContent,
      imageFiles,
      uploadedImageURLs,
      isUploadingImage,
      pastedSourceText,
      imageAnalyses,
      attachedLinks,
      showLinkModal,
      linkInput,
      showYoutubeTranscriptModal,
      youtubeModalUrl,
    ]
  );
};
